/**
 * Case Study Service for Hugo_CaseStudies collection.
 * Uses modular Firebase SDK v9+ with tree-shaking support.
 */
import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { collections } from '../utils/firestore';

/**
 * Fetch all published, globally-visible case studies.
 * @returns {Promise<Array<{id: string, ...data}>>}
 */
export async function getPublishedCaseStudies() {
  const ref = collection(db, collections.caseStudies);
  const q = query(
    ref,
    where('visibility', '==', 'global'),
    where('status', '==', 'published'),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Fetch a single case study by ID.
 * @param {string} caseStudyId
 * @returns {Promise<{id: string, ...data} | null>}
 */
export async function getCaseStudyById(caseStudyId) {
  const ref = doc(db, collections.caseStudies, caseStudyId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Seed the Chevron Richmond Refinery case study into Hugo_CaseStudies.
 * Uses setDoc with a deterministic ID so it can be run multiple times safely (idempotent).
 * Requires superadmin authentication (enforced by Firestore rules).
 *
 * Source: CSB Final Investigation Report 2012-03-I-CA, January 2015
 *
 * @returns {Promise<string>} The document ID of the seeded case study
 */
export async function seedChevronCaseStudy() {
  const docId = 'chevron-richmond-refinery-2012';

  const caseStudyData = {
    sourceDocumentId: null,
    organizationId: null,
    title: 'Chevron Richmond Refinery Fire — CSB Investigation Analysis',
    summary:
      'On August 6, 2012, a catastrophic pipe failure at Chevron\'s Richmond, California refinery released flammable hydrocarbon process fluid which ignited, ' +
      'creating a large vapor cloud fire that sent more than 15,000 community members to local hospitals. The U.S. Chemical Safety and Hazard Investigation Board (CSB) ' +
      'investigation revealed systemic organizational failures spanning over a decade, including the normalization of deviance around corrosion risk, ' +
      'ignored safety recommendations, and inadequate mechanical integrity programs.\n\n' +
      'The CSB\'s final investigation report (2012-03-I-CA, January 2015) identified that Chevron had received repeated warnings about the vulnerability of ' +
      'low-silicon carbon steel piping to sulfidation corrosion in high-temperature crude oil service, yet failed to replace the pipe that ultimately ruptured. ' +
      'The incident exemplifies how organizational entropy — particularly drift in perception and drift in work practices — can accumulate over years until a ' +
      'catastrophic event occurs.\n\n' +
      'This case study maps the incident to the REPS (Reliability, Efficiency, Productivity, Safety) framework and the five Organizational Entropy (OE) elements, ' +
      'providing a reference model for detecting similar precursor patterns in other organizations.',
    incidentDate: '2012-08-06',
    industry: 'oil_gas',
    source: 'csb',

    repsMapping: {
      reliability: 9,
      efficiency: 5,
      productivity: 4,
      safety: 10,
    },

    oeElements: {
      driftPerception: {
        score: 9,
        findings: [
          'Sulfidation corrosion risk was normalized over 10+ years despite known vulnerability of low-silicon carbon steel',
          'Repeated thickness measurements showing thinning were not acted upon with appropriate urgency',
          'Risk perception shifted from "critical replacement needed" to "monitor and manage" without engineering justification',
          'Organizational memory of prior sulfidation incidents at other Chevron facilities was not effectively transferred',
          'The 4-sidecut piping system was not included in the damage mechanism review (DMR) until after the incident',
        ],
      },
      driftWork: {
        score: 8,
        findings: [
          'Equipment Technical Committee (ETC) recommendations for pipe replacement were repeatedly ignored or deferred',
          'Inspection intervals were extended without adequate justification based on remaining corrosion allowance',
          'Industry best practices (API 939-C) for sulfidation corrosion management were not followed',
          'Metallurgical analysis recommendations for silicon content verification were not implemented fleet-wide',
          'Work practices drifted from replacement-focused to patch-and-monitor approach',
        ],
      },
      traps: {
        identified: [
          'Time pressure — production demands overrode maintenance scheduling for pipe replacement',
          'Overconfidence — belief that monitoring alone could manage a known degradation mechanism',
          'Vague guidance — corporate sulfidation guidelines lacked specific action triggers for replacement',
          'Peer pressure — culture discouraged challenging decisions to continue operating degraded equipment',
          'Complacency — absence of prior failure at this specific location created false sense of security',
        ],
        scores: {
          time_pressure: 8,
          overconfidence: 9,
          vague_guidance: 7,
          peer_pressure: 6,
          complacency: 9,
        },
      },
      roadblocks: {
        identified: [
          'Framing Document gatekeeping — a Chevron-specific administrative process required a "Framing Document" to justify capital projects, creating a bureaucratic barrier that delayed and ultimately blocked pipe replacement',
          'Budget approval process prioritized cost avoidance over safety investment',
          'Organizational siloing between inspection, engineering, and operations prevented integrated risk assessment',
          'Lack of clear escalation path when safety recommendations were repeatedly deferred',
        ],
        score: 8,
      },
      landmines: {
        identified: [
          'Low-silicon carbon steel vulnerability — piping installed decades earlier with carbon steel containing <0.10% silicon, making it highly susceptible to sulfidation corrosion at operating temperatures',
          'Hidden wall thinning — corrosion occurred on internal pipe surfaces, not visible during external inspections without ultrasonic thickness measurements',
          'Inconsistent material records — historical records did not reliably capture silicon content of installed piping',
          'Process condition changes — crude slate changes over time altered sulfur content and corrosion rates without corresponding reassessment',
        ],
        score: 9,
      },
    },

    keyFindings: [
      {
        finding: 'Chevron received repeated safety recommendations from its own Equipment Technical Committee to replace the corroded pipe section, but these were ignored or deferred for over a decade',
        severity: 'critical',
        oeElement: 'driftWork',
        repsQuadrant: 'safety',
      },
      {
        finding: 'The Framing Document process acted as a gatekeeping mechanism that effectively blocked safety-critical capital expenditures by requiring extensive financial justification',
        severity: 'critical',
        oeElement: 'roadblocks',
        repsQuadrant: 'reliability',
      },
      {
        finding: 'Low-silicon carbon steel piping vulnerability to sulfidation corrosion was a known industry issue, yet Chevron did not systematically identify and replace all vulnerable piping',
        severity: 'critical',
        oeElement: 'landmines',
        repsQuadrant: 'safety',
      },
      {
        finding: 'Stop Work Authority (SWA) culture issues prevented workers from challenging the decision to continue operating with known degraded piping',
        severity: 'major',
        oeElement: 'traps',
        repsQuadrant: 'safety',
      },
      {
        finding: 'Normalization of deviance — the gradual acceptance of increasing risk as corrosion progressed became the organizational norm rather than the exception',
        severity: 'critical',
        oeElement: 'driftPerception',
        repsQuadrant: 'reliability',
      },
    ],

    lessonsLearned: [
      {
        lesson: 'Safety recommendations from technical committees need formal escalation paths with accountability — when recommendations are deferred, the rationale and risk acceptance must be documented and reviewed by senior leadership',
        applicability: 'All organizations with technical advisory committees',
        priority: 'high',
      },
      {
        lesson: 'Inspection practices must match expert guidance for known damage mechanisms — relying on general inspection programs without damage-mechanism-specific protocols creates blind spots',
        applicability: 'Process industries with corrosion-susceptible equipment',
        priority: 'high',
      },
      {
        lesson: 'Budget gatekeeping processes must not override safety-critical maintenance — administrative barriers to capital expenditure should have fast-track provisions for safety-related work',
        applicability: 'Organizations with capital approval processes',
        priority: 'high',
      },
      {
        lesson: 'Multi-person review for critical calculations and assessments — fitness-for-service evaluations and remaining-life calculations for safety-critical equipment should require independent verification',
        applicability: 'Engineering and inspection organizations',
        priority: 'medium',
      },
    ],

    precursorPatterns: [
      {
        patternType: 'safety_recommendation_ignored',
        description: 'Safety-critical recommendations from technical experts are repeatedly deferred or not implemented within recommended timeframes',
        matchCriteria: {
          element: 'driftWork',
          operator: 'gt',
          value: 7,
        },
      },
      {
        patternType: 'normalization_of_deviance',
        description: 'Gradual acceptance of degraded conditions as normal, with risk perception declining over time despite unchanged or worsening physical conditions',
        matchCriteria: {
          element: 'driftPerception',
          operator: 'gt',
          value: 7,
        },
      },
      {
        patternType: 'inspection_gap',
        description: 'Inspection programs do not adequately cover known damage mechanisms, or inspection intervals are extended without engineering justification',
        matchCriteria: {
          element: 'driftWork',
          operator: 'gt',
          value: 6,
        },
      },
      {
        patternType: 'swa_reluctance',
        description: 'Workers are reluctant to exercise Stop Work Authority due to cultural pressure, time constraints, or fear of repercussions',
        matchCriteria: {
          element: 'traps',
          operator: 'gt',
          value: 6,
        },
      },
    ],

    tags: [
      'csb',
      'oil_gas',
      'refinery',
      'sulfidation',
      'mechanical_integrity',
      'process_safety',
      'normalization_of_deviance',
      'stop_work_authority',
    ],

    visibility: 'global',
    status: 'published',
    createdAt: Timestamp.now(),
    reviewedBy: null,
  };

  const docRef = doc(db, collections.caseStudies, docId);
  await setDoc(docRef, caseStudyData);

  return docId;
}
